import { LoaderCircle } from 'lucide-react';
import { createPortal } from 'react-dom';

type FullScreenLoadingOverlayProps = {
  isVisible: boolean;
  label: string;
  /**
   * When true, the label is followed by three sequentially blinking dots.
   * The `label` should not include its own trailing ellipsis in that case.
   */
  animatedEllipsis?: boolean;
};

export function FullScreenLoadingOverlay({
  isVisible,
  label,
  animatedEllipsis = false,
}: FullScreenLoadingOverlayProps) {
  if (!isVisible) {
    return null;
  }

  const overlay = (
    <div
      aria-label={label}
      aria-live="polite"
      className="fixed inset-0 z-[150] flex items-center justify-center bg-stone-950/25 px-6 backdrop-blur-[1px]"
      role="status"
    >
      <div className="flex items-center gap-3 rounded-full border border-white/75 bg-white/90 px-5 py-3 text-sm font-medium text-stone-900 shadow-[0_20px_50px_rgba(28,25,23,0.18)]">
        <LoaderCircle className="size-5 animate-spin text-emerald-600" />
        <span>
          {label}
          {animatedEllipsis ? (
            <span aria-hidden="true" className="loading-dots">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          ) : null}
        </span>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return overlay;
  }

  return createPortal(overlay, document.body);
}
